import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  cancelJourneyStore,
  type CancelJourneyContext,
  type CancelReason,
  type ProviderIntent,
} from '../lib/subscription-journey/cancel-journey';

/**
 * The cancel journey's shared state. It lives in a module store (not React
 * state) because native pushes one settings route per step; every step reads
 * the same journey.
 *
 * Lifecycle:
 * - E (reason screen) mounts -> `start(getCancellationScope(snapshot, companyId),
 *   { tier, accountCycle: snapshot.billingInterval })`, then, once loaded,
 *   `setContext({ capabilities, offer })` from `useRetentionOptions`.
 *   Re-entering E for the same scope keeps the reason, chip and drafts.
 * - Each step reads `state` and routes with `next*()` helpers.
 * - Closing the journey (sheet close / "Keep my plan" / "Back to my plan")
 *   calls `clear()`. Also clear when `canStartCancellation(snapshot)` turns
 *   false or the scope changes.
 */
export function useCancelJourney() {
  const state = useSyncExternalStore(
    cancelJourneyStore.subscribe,
    cancelJourneyStore.getState,
    cancelJourneyStore.getState,
  );

  const start = useCallback(
    (scope: string, context?: Partial<CancelJourneyContext>) =>
      cancelJourneyStore.start(scope, context),
    [],
  );

  return useMemo(
    () => ({
      state,
      draft: state.reason ? (state.drafts[state.reason] ?? '') : '',
      start,
      setContext: (context: Partial<CancelJourneyContext>) =>
        cancelJourneyStore.setContext(context),
      selectReason: (reason: CancelReason) =>
        cancelJourneyStore.selectReason(reason),
      selectDetail: (detail: string | null) =>
        cancelJourneyStore.selectDetail(detail),
      setDraft: (text: string) => cancelJourneyStore.setDraft(text),
      setProviderIntent: (intent: ProviderIntent) =>
        cancelJourneyStore.setProviderIntent(intent),
      nextAfterReason: () => cancelJourneyStore.nextAfterReason(),
      nextAfterHelp: () => cancelJourneyStore.nextAfterHelp(),
      nextAfterCareer: () => cancelJourneyStore.nextAfterCareer(),
      nextAfterPauseDeclined: () => cancelJourneyStore.nextAfterPauseDeclined(),
      clear: () => cancelJourneyStore.clear(),
    }),
    [start, state],
  );
}
